/**
 * Trap queue management.
 *
 * Incoming traps are queued in VersusMatchState.pendingTraps.
 * This module drains the queue each frame:
 * - GROOVE_DRAIN applies immediately for reliable groove interruption.
 * - Other traps apply only when local controller is IDLE.
 *
 * Called every frame from VersusController.update().
 */

import type { GameController } from "../game/stateMachine";
import type { VersusMatchState } from "./types";
import { applyTrapEffect } from "./trapEffects";

/**
 * Check whether the controller is in a safe "idle" state for trap
 * application: phase is RUN_PLAYABLE and no animations or powerup
 * operations are in progress.
 *
 * We check public fields only — phase and run existence.
 * Animation state is private, so we expose a lightweight proxy:
 * controller phase RUN_PLAYABLE implies cascades/animations have settled.
 */
function isControllerIdle(ctrl: GameController): boolean {
  return ctrl.phase === "RUN_PLAYABLE" && ctrl.run != null;
}

/**
 * Process pending traps for the local player.
 *
 * @returns The fog duration to add (seconds) if a FOG_OVERLAY trap was applied,
 *          otherwise 0. The caller tracks the fog timer on VersusController state.
 */
export function processPendingTraps(
  localCtrl: GameController,
  matchState: VersusMatchState
): number {
  if (matchState.pendingTraps.length === 0) return 0;
  const run = localCtrl.run;
  if (run == null) return 0;

  let fogDuration = 0;
  const shouldApplyIdleGated = isControllerIdle(localCtrl);
  const deferred: typeof matchState.pendingTraps = [];

  // Drain all queued traps once per frame:
  // - GROOVE_DRAIN applies immediately to guarantee interruption reliability.
  // - Other traps stay idle-gated to avoid mid-animation state mutation.
  while (matchState.pendingTraps.length > 0) {
    const trap = matchState.pendingTraps.shift()!;
    const applyImmediately = trap.kind === "GROOVE_DRAIN";
    if (!applyImmediately && !shouldApplyIdleGated) {
      deferred.push(trap);
      continue;
    }
    fogDuration += applyTrapEffect(
      trap.kind,
      run,
      matchState.seed,
      trap.seq,
      localCtrl
    );
  }
  if (deferred.length > 0) {
    matchState.pendingTraps.push(...deferred);
  }

  return fogDuration;
}
